import type { ReactNode } from 'react';
import { Link, useParams } from 'react-router';
import { useMutation, useQuery } from '@apollo/client/react';
import { ArrowLeft } from 'lucide-react';
import { graphql } from '@/gql';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { EmptyState } from '@/components/ui/EmptyState';
import { formatCurrency, formatDate, formatDateTime } from '@/lib/format';
import { useBackHref } from '@/hooks/useListLocation';

const ExactJournalEntryDoc = graphql(/* GraphQL */ `
  query ExactJournalEntry($id: UUID!) {
    exactJournalEntry(id: $id) {
      id
      recognitionDate
      status
      journalCode
      description
      financialYear
      financialPeriod
      exactEntryId
      entryNumber
      error
      createdAt
      updatedAt
      lines {
        id
        lineNumber
        glAccountCode
        glAccountDescription
        description
        amount
        vatCode
        vatPercentage
        vatAmount
      }
    }
  }
`);

const ExportExactDayDoc = graphql(/* GraphQL */ `
  mutation ExportExactDayRetry($recognitionDate: String!) {
    exportExactDay(recognitionDate: $recognitionDate) {
      id
      status
      error
    }
  }
`);

function Row({ label, id, children }: { label: string; id?: string; children: ReactNode }) {
  return (
    <div className="flex justify-between gap-4">
      <dt className="text-gray-500 dark:text-gray-400">{label}</dt>
      <dd id={id} className="text-right text-gray-900 dark:text-white">{children}</dd>
    </div>
  );
}

/**
 * Detail view of one Exact Online journal entry and the lines that were sent.
 */
export function ExactEntryDetailPage() {
  const backHref = useBackHref('/exact');
  const { id = '' } = useParams();
  const { data, previousData, loading, error, refetch } = useQuery(ExactJournalEntryDoc, {
    variables: { id },
    skip: !id,
  });
  const entry = (data ?? previousData)?.exactJournalEntry;
  const [exportDay, { loading: retrying }] = useMutation(ExportExactDayDoc);

  if (loading && !entry) {
    return <div className="p-10 text-center text-sm text-gray-500">Loading…</div>;
  }
  if (error) {
    return <EmptyState title="Could not load entry" description={error.message} />;
  }
  if (!entry) {
    return (
      <EmptyState
        title="Entry not found"
        description="This Exact booking does not exist or has been deleted."
      />
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Link to={backHref} id="admin.exact.entries.back">
            <Button variant="outline" size="sm" leftIcon={<ArrowLeft className="h-4 w-4" />}>
              Back to Exact
            </Button>
          </Link>
          <div>
            <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">
              {formatDate(entry.recognitionDate)}
            </h1>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              Exact Online memoriaal · journal {entry.journalCode}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Badge id="admin.exact.entry.status" tone={entry.status === 'succeeded' ? 'success' : 'error'}>
            {entry.status}
          </Badge>
          {entry.status === 'failed' && (
            <Button
              size="sm"
              variant="outline"
              loading={retrying}
              onClick={() =>
                void exportDay({ variables: { recognitionDate: entry.recognitionDate } }).then(
                  () => refetch(),
                )
              }
            >
              Retry
            </Button>
          )}
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card title="Summary">
          <dl className="space-y-2 text-sm">
            <Row label="Recognition day">{formatDate(entry.recognitionDate)}</Row>
            <Row label="Journal">{entry.journalCode}</Row>
            <Row label="Description">{entry.description}</Row>
            <Row label="Period">
              {entry.financialYear}-{String(entry.financialPeriod).padStart(2, '0')}
            </Row>
            <Row label="Exact entry #" id="admin.exact.entry.number">{entry.entryNumber ?? '—'}</Row>
            <Row label="Exact entry ID" id="admin.exact.entry.entryId">{entry.exactEntryId ?? '—'}</Row>
            <Row label="Created">{formatDateTime(entry.createdAt)}</Row>
            <Row label="Updated">{formatDateTime(entry.updatedAt)}</Row>
          </dl>
          {entry.error && (
            <p className="mt-4 rounded-lg bg-red-50 p-3 text-xs text-red-700 dark:bg-red-950/40 dark:text-red-300">
              {entry.error}
            </p>
          )}
        </Card>

        <Card title="Lines sent to Exact" padded={false}>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="border-b border-gray-200 bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-500 dark:border-white/10 dark:bg-gray-800/50 dark:text-gray-400">
                <tr>
                  <th className="px-4 py-2">#</th>
                  <th className="px-4 py-2">GL</th>
                  <th className="px-4 py-2">VAT</th>
                  <th className="px-4 py-2 text-right">Amount</th>
                  <th className="px-4 py-2 text-right">VAT amt</th>
                </tr>
              </thead>
              <tbody>
                {entry.lines.map((line, index) => (
                  <tr
                    id={`admin.exact.entry.line.${index}`}
                    key={line.id}
                    className="border-b border-gray-100 last:border-0 dark:border-white/5"
                  >
                    <td className="px-4 py-2 text-gray-500">{line.lineNumber}</td>
                    <td className="px-4 py-2">
                      <div className="font-medium text-gray-900 dark:text-white">
                        {line.glAccountCode}
                      </div>
                      <div className="text-xs text-gray-500">{line.glAccountDescription}</div>
                    </td>
                    <td className="px-4 py-2 text-gray-600 dark:text-gray-300">
                      {line.vatCode
                        ? `${line.vatCode}${line.vatPercentage != null ? ` (${line.vatPercentage}%)` : ''}`
                        : '—'}
                    </td>
                    <td
                      id={`admin.exact.entry.line.${index}.amount`}
                      className="px-4 py-2 text-right tabular-nums"
                    >
                      {formatCurrency(line.amount)}
                    </td>
                    <td
                      id={`admin.exact.entry.line.${index}.vatAmount`}
                      className="px-4 py-2 text-right tabular-nums"
                    >
                      {line.vatAmount != null ? formatCurrency(line.vatAmount) : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      </div>
    </div>
  );
}
